import Drawer from '@material-ui/core/Drawer';
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import Input from '@wandb/weave/common/components/Input';
import {TargetBlank} from '@wandb/weave/common/util/links';
import * as String from '@wandb/weave/common/util/string';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Option} from '@wandb/weave/common/util/uihelpers';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Loading} from '@wandb/weave/components/Loading';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import React, {memo, useCallback, useContext, useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {InputOnChangeData} from 'semantic-ui-react';

import {
  OrganizationMembersTableQuery,
  OrganizationSubscriptionInfoQuery,
  useAddUserToOrganizationMutation,
  useCreateInviteMutation,
} from '../generated/graphql';
import {useHandleScroll} from '../pages/HomePage/HomePageSidebar/useHandleScroll';
import {Analytics} from '../services/analytics';
import {
  getPrimarySub,
  orgPrimarySubUnitPrice,
  useAccountPrivileges,
} from '../util/accounts/pricing';
import {getErrMsgFromError, propagateErrorsContext} from '../util/errors';
import * as urls from '../util/urls';
import {Banner, BannerFlexWrapper} from './Banner';
import {ResizableDrawer} from './Launch/ResizableDrawer';
import {RoleDropdown} from './MembersTable/AccountSettingsMembersTable/RoleDropdown';
import {useGetRoleOptions} from './MembersTable/TeamMembersTable/useGetRoleOptions';
import {TeamMemberRole} from './MembersTable/TeamMembersTable/utils';
import {RoleOption} from './MembersTable/TeamOrOrganizationRoleOption';
import {
  getInviteRoleOptions,
  OrganizationMemberRole,
} from './MembersTable/util';
import {AccountSelectorContext} from './Search/SearchNav/AccountSelectorContextProvider';

type AddUserOrganization = Pick<
  NonNullable<OrganizationMembersTableQuery['organization']>,
  'teams' | 'name' | 'id' | 'seatAvailability' | 'subscriptions'
>;

type AddUserPropsBase = {
  open: boolean;
  toggleTeamDrawer: () => void;
  analyticsLocation: string;
  refetch?: () => void;
  organization: AddUserOrganization;
  roleOptionsList: RoleOption[];
  isWeaveAsAServiceUser: boolean;
};

type TeamInviteProps =
  | {isTeamInvite: true; existingTeamName: string}
  | {isTeamInvite: false};

type AddExistingUserProps =
  | {isAddingExistingUser: true; existingOrgMember: string}
  | {isAddingExistingUser: false};

type AddUserProps = AddUserPropsBase & TeamInviteProps & AddExistingUserProps;

export const AddUserDrawerComp = ({
  open,
  toggleTeamDrawer,
  analyticsLocation,
  refetch,
  organization,
  roleOptionsList,
  isTeamInvite,
  isAddingExistingUser,
  isWeaveAsAServiceUser,
  ...conditionalProps
}: AddUserProps) => {
  const {scrolled} = useHandleScroll();

  const [width, setWidth] = useState(600);
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const onClose = (e: React.MouseEvent) => {
    clearInputState();
    toggleTeamDrawer();
  };

  const teamOptions = getTeamOptions(organization);

  const enabledRoleOptions = isTeamInvite
    ? roleOptionsList.filter(
        roleOption =>
          (!roleOption.isPremiumOption ||
            !roleOption.isPremiumFeatureDisabled) &&
          roleOption.viewerCanInviteOtherAsRole
      )
    : roleOptionsList;

  const defaultRole =
    (enabledRoleOptions.find(
      roleOption =>
        !roleOption.isPremiumOption || !roleOption.isPremiumFeatureDisabled
    )?.value as TeamMemberRole | OrganizationMemberRole) ??
    (isTeamInvite
      ? (TeamMemberRole.MEMBER as TeamMemberRole)
      : (OrganizationMemberRole.ADMIN as OrganizationMemberRole));

  const existingTeamName = isTeamInvite
    ? (conditionalProps as {existingTeamName: string}).existingTeamName
    : '';
  const existingOrgMember = isAddingExistingUser
    ? (conditionalProps as {existingOrgMember: string}).existingOrgMember
    : '';

  const [emailOrUsername, setEmailOrUsername] = useState(existingOrgMember);
  const [teams, setTeams] = useState<string[]>(
    isTeamInvite ? [existingTeamName] : []
  );
  const [role, setRole] = useState(defaultRole);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showContactSalesOnError, setShowContactSalesOnError] = useState(false);

  const clearInputState = useCallback(() => {
    setEmailOrUsername('');
    setTeams(teams);
    setRole(defaultRole);
    setErrorMsg(null);
    setShowContactSalesOnError(false);
  }, [defaultRole, teams]);

  const {addMemberToOrganization, addMemberToTeam} = useAddMember({
    organization,
    teams,
    emailOrUsername,
    role,
    analyticsLocation,
    existingTeamName,
    refetch,
    toggleTeamDrawer,
    setErrorMsg,
    setShowContactSalesOnError,
    clearInputState,
  });

  const onAddMemberClick = async () => {
    if (emailOrUsername === '' || teams.length === 0) {
      setErrorMsg(`Please fill out all the fields to add a member.`);
      return;
    }

    if (isTeamInvite) {
      await addMemberToTeam();
    } else {
      await addMemberToOrganization();
    }
  };

  const padding = scrolled ? '0px 20px 20px 20px' : '60px 20px 20px 20px';

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <Tailwind>
          <div className="flex h-screen flex-col" style={{padding}}>
            <div className="flex-1">
              <div className="flex h-60 items-center">
                <div className="font-source-sans-pro text-2xl font-semibold leading-10">
                  {existingOrgMember != null
                    ? 'Add a user to a team'
                    : 'Invite new user'}
                </div>
                <div className="flex-shrink flex-grow basis-auto text-right">
                  <Button
                    onClick={onClose}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>
              <div className="-ml-20 -mr-20 h-px bg-moon-250" />
              <Banner
                variant="info"
                data-test="create-team-models-info-banner"
                eventData={{location: analyticsLocation}}
                className="mb-10 mt-10">
                <BannerFlexWrapper>
                  <div className="align-center flex text-xs">
                    <Icon name="lightbulb-info" />
                  </div>
                  <div className="inline-flex flex-col gap-1 text-base">
                    Once the user accepts the invitation, their access to Models
                    can be adjusted. By default, they'll receive a full models
                    seat, if available.
                  </div>
                </BannerFlexWrapper>
              </Banner>
              {errorMsg && (
                <Banner
                  variant="error"
                  data-test="create-team-errors"
                  eventData={{location: analyticsLocation}}
                  className="mb-10 mt-10">
                  <BannerFlexWrapper>
                    <div className="align-center flex text-xs">
                      <Icon name="warning" />
                    </div>
                    <div className="inline-flex flex-col gap-1 text-base">
                      {errorMsg}
                      {showContactSalesOnError && (
                        <TargetBlank
                          href={urls.contactSalesSeats()}
                          onClick={() => {
                            Analytics.track(
                              'Contact sales clicked from invite drawer',
                              {
                                organizationName: organization.name,
                                location: analyticsLocation,
                                error: errorMsg,
                              }
                            );
                          }}>
                          Contact sales in order to purchase more.
                        </TargetBlank>
                      )}
                    </div>
                  </BannerFlexWrapper>
                </Banner>
              )}

              <div className="mt-10 flex flex-col justify-start">
                <label
                  htmlFor="addUserDrawerEmailOrUsername"
                  className="mb-1 block font-semibold">
                  Email or username
                </label>
                <Input
                  id="addUserDrawerEmailOrUsername"
                  style={{
                    flexGrow: 2,
                  }}
                  disabled={isAddingExistingUser}
                  data-test="add-member-input"
                  placeholder={
                    isAddingExistingUser
                      ? existingOrgMember
                      : 'Email or username'
                  }
                  value={emailOrUsername}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    {value}: InputOnChangeData
                  ) => setEmailOrUsername(value)}
                />
              </div>

              <div className="mt-10 flex flex-col justify-start">
                <label className="mb-1 block font-semibold">
                  {isTeamInvite ? 'Team' : 'Choose team(s)'}
                </label>
                <ModifiedDropdown
                  className="mr-2"
                  style={{flexGrow: 2, minWidth: '150px', boxShadow: 'none'}}
                  data-test="add-member-teams-dropdown"
                  options={teamOptions}
                  value={teams}
                  disabled={isTeamInvite}
                  placeholder={
                    isTeamInvite ? existingTeamName : 'Select team(s)'
                  }
                  fluid
                  multiple
                  search
                  selection
                  onChange={(e, {value}) => {
                    setTeams(value as string[]);
                  }}
                />
              </div>

              <div className="mt-10 flex flex-col justify-start">
                <label className="mb-1 block font-semibold">
                  {isTeamInvite || isAddingExistingUser
                    ? 'Select team role'
                    : 'Select role'}
                </label>
                <RoleDropdown
                  role={role}
                  roleOptions={enabledRoleOptions}
                  setRole={setRole}
                />
              </div>
            </div>
            <Button
              className="w-full"
              data-test="add-member-button"
              variant="primary"
              size="large"
              onClick={onAddMemberClick}>
              {isAddingExistingUser ? 'Add user' : 'Send invite'}
            </Button>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};

function getTeamOptions(
  organization: Pick<AddUserOrganization, 'teams'> | undefined
): Option[] {
  const teamNames = (organization?.teams ?? [])
    .map(team => team?.name)
    .filter(isNotNullOrUndefined);
  const sortedTeamNames = _.sortBy(teamNames);

  return sortedTeamNames.map(teamName => {
    return {text: teamName, value: teamName};
  });
}

type UseAddMemberProps = {
  organization: AddUserOrganization;
  teams: string[];
  emailOrUsername: string;
  role: OrganizationMemberRole | TeamMemberRole;
  analyticsLocation: string;
  existingTeamName: string;
  refetch: (() => void) | undefined;
  toggleTeamDrawer: () => void;
  setErrorMsg: React.Dispatch<React.SetStateAction<string | null>>;
  setShowContactSalesOnError: React.Dispatch<React.SetStateAction<boolean>>;
  clearInputState: () => void;
};

const useAddMember = ({
  organization,
  teams,
  emailOrUsername,
  role,
  analyticsLocation,
  existingTeamName,
  refetch,
  toggleTeamDrawer,
  setErrorMsg,
  setShowContactSalesOnError,
  clearInputState,
}: UseAddMemberProps) => {
  const [addUserToOrganizationMutation] = useAddUserToOrganizationMutation({
    context: propagateErrorsContext(),
  });

  const [createInvite] = useCreateInviteMutation({
    context: propagateErrorsContext(),
  });

  const primarySub = getPrimarySub(organization);
  const planUnitPrice = orgPrimarySubUnitPrice(organization);
  const availableSeatsToPurchase = primarySub?.availableSeatsToPurchase ?? 0;
  const canPurchaseMoreSeat =
    planUnitPrice != null && availableSeatsToPurchase > 0;

  const addMemberToOrganization = async () => {
    if (!role) {
      return;
    }

    Analytics.track('Add Organization Member Clicked', {
      entityNames: teams,
      organizationName: organization?.name,
      location: analyticsLocation,
    });

    try {
      const addUserResult = await addUserToOrganizationMutation({
        variables: {
          userName: emailOrUsername.trim(),
          teams,
          organizationId: organization?.id || '',
          userOrgRole: role,
        },
      });

      if (addUserResult.errors) {
        const errMsg = addUserResult.errors.map(e => e.message).join(', ');
        setErrorMsg(`Error adding user: ${errMsg}`);
        Analytics.track('Invite Error Viewed', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          error: errMsg,
        });
      } else if (
        addUserResult.data &&
        !addUserResult.data.addUserToOrganization?.success
      ) {
        const errMessage = `Error adding user: please contact support`;
        setErrorMsg(errMessage);
        Analytics.track('Invite Error Viewed', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          error: errMessage,
        });
      } else if (
        addUserResult.data &&
        addUserResult.data.addUserToOrganization?.success
      ) {
        clearInputState();
        Analytics.track('New Org Member Added', {
          organizationName: organization?.name,
          entityNames: teams,
          location: analyticsLocation,
          role,
          organizationId: organization?.id,
          usernameOrEmail: emailOrUsername,
        });
        toggleTeamDrawer();
        refetch?.();
        toast('Successfully added user to organization.');
      }
    } catch (err) {
      const errMsg = getErrMsgFromError(err);
      setErrorMsg(
        `Error adding user to organization${errMsg ? `: ${errMsg}` : ``}`
      );
      Analytics.track('Invite Error Viewed', {
        organizationName: organization?.name,
        entityNames: teams,
        location: analyticsLocation,
        error: errMsg,
      });
    }
  };

  const addMemberToTeam = async () => {
    if (!existingTeamName) {
      return;
    }

    Analytics.track('Invite Submitted', {
      entityName: existingTeamName,
      organizationName: organization?.name,
      location: analyticsLocation,
    });

    const doCreateInvite = async () => {
      try {
        const createInviteResult = await createInvite({
          variables: {
            entityName: existingTeamName,
            role,
            [String.isValidEmail(emailOrUsername) ? 'email' : 'username']:
              emailOrUsername.trim(),
          },
        });

        if (createInviteResult.errors != null) {
          setErrorMsg(createInviteResult.errors.map(e => e.message).join(', '));
          clearInputState();
          return;
        }

        const inviteData = createInviteResult.data?.createInvite;

        let remainingSeats = false;
        if (inviteData) {
          const {remainingSeats: seats, remainingViewOnlySeats: viewSeats} =
            inviteData;
          remainingSeats =
            (seats != null && seats > 0) ||
            (viewSeats != null && viewSeats > 0);
        }

        if (!createInviteResult.data?.createInvite?.success) {
          setErrorMsg(`Error adding user to org. Please contact support`);
          setShowContactSalesOnError(!canPurchaseMoreSeat);
        } else {
          clearInputState();
          toggleTeamDrawer();
          refetch?.();
          toast('Successfully added user to team.');
        }
      } catch (err) {
        const errMsg = getErrMsgFromError(err);
        setErrorMsg(errMsg);
      }
    };

    await doCreateInvite();
  };

  return {addMemberToOrganization, addMemberToTeam};
};

type AddUserButtonProps = {
  disabled: boolean;
  refetch?: () => void;
  existingTeamName?: string | null;
  analyticsLocation: string;
  organization: NonNullable<OrganizationSubscriptionInfoQuery['organization']>;
};

export const AddUserButton = ({
  disabled,
  refetch,
  existingTeamName,
  analyticsLocation,
  organization,
}: AddUserButtonProps) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const toggleAddUserDrawer = useCallback(() => {
    setIsAddingUser(!isAddingUser);
  }, [isAddingUser]);

  const isTeamLevelInvite = existingTeamName != null;

  const isViewOnlyAvailable =
    (organization?.seatAvailability?.viewOnlySeats ?? 0) > 0;
  const {privileges} = useAccountPrivileges(selectedAccount);

  const orgLevelInviteRoleOptions = getInviteRoleOptions(
    isViewOnlyAvailable,
    privileges.roleManagementEnabled
  );

  const {roleOptions: teamLevelInviteRoleOptions} = useGetRoleOptions(
    existingTeamName ?? ''
  );

  if (organization === undefined) {
    return <Loading></Loading>;
  }

  const roleOptions = isTeamLevelInvite
    ? teamLevelInviteRoleOptions
    : orgLevelInviteRoleOptions;

  return (
    <>
      <Button
        className="font-normal"
        data-test="add-team-invite-button"
        startIcon="add-new"
        variant={'ghost'}
        disabled={disabled}
        tooltip={disabled ? 'Buy more seats in order to invite new users' : ''}
        onClick={toggleAddUserDrawer}>
        Invite new user
      </Button>
      <AddUserDrawerComp
        open={isAddingUser}
        toggleTeamDrawer={toggleAddUserDrawer}
        analyticsLocation={analyticsLocation}
        refetch={refetch}
        organization={organization}
        existingTeamName={
          isTeamLevelInvite && existingTeamName ? existingTeamName : ''
        }
        roleOptionsList={roleOptions}
        isTeamInvite={isTeamLevelInvite}
        isAddingExistingUser={false}
        isWeaveAsAServiceUser
      />
    </>
  );
};

export const AddUserDrawer = memo(AddUserButton);
